import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, ChevronDown, Search } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    trackPageView('/faq', 'FAQ');
    fetch(`${BACKEND_URL}/api/faq`).then(r => r.json()).then(setFaqs).catch(() => {});
  }, []);

  const categories = ['All', ...new Set(faqs.map(f => f.category))];
  const filtered = faqs.filter(f => {
    const matchCat = filter === 'All' || f.category === filter;
    const matchSearch = !search || f.question.toLowerCase().includes(search.toLowerCase()) || f.answer.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#0a0a0b] pt-28 pb-20 px-4" data-testid="faq-page">
      <SEOHead
        title="Frequently Asked Questions"
        description="Answers to common questions about cement grades (OPC vs PPC), construction techniques, AAC blocks, dealership, and Avi Cement products."
        path="/faq"
      />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto text-center mb-10">
        <span className="text-[#E85A1C] text-xs font-semibold tracking-[0.2em] uppercase">FAQ</span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 mb-4">
          Aksar Poochhe Jaane Wale <span className="text-[#F5A623]">Sawal</span>
        </h1>
        <p className="text-gray-400 text-sm max-w-md mx-auto">Cement, construction, aur dealership se related common questions ke answers.</p>
      </motion.div>

      <div className="max-w-3xl mx-auto mb-8">
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search questions..."
            data-testid="faq-search"
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-[#111113] border border-gray-800 text-white text-sm focus:outline-none focus:border-[#E85A1C]" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilter(cat)} data-testid={`faq-filter-${cat.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${filter === cat ? 'bg-[#E85A1C] text-white' : 'bg-[#111113] text-gray-400 hover:text-white border border-gray-800'}`}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto space-y-3" data-testid="faq-list">
        {filtered.map((faq, idx) => (
          <motion.div key={faq.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
            className="bg-[#111113] border border-gray-800 rounded-xl overflow-hidden" data-testid={`faq-item-${faq.id}`}>
            <button onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
              className="w-full flex items-start gap-3 p-5 text-left">
              <HelpCircle className="w-5 h-5 text-[#E85A1C] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <span className="text-white text-sm font-medium">{faq.question}</span>
                <span className="text-[#F5A623] text-xs ml-2">{faq.category}</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${openId === faq.id ? 'rotate-180' : ''}`} />
            </button>
            {openId === faq.id && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="px-5 pb-5 pl-13">
                <p className="text-gray-400 text-sm leading-relaxed ml-8">{faq.answer}</p>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default FAQPage;
